// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
// DemoDataScrubber — Automated demo account data cleanup with proof-of-deletion.
//
// 3-Tier Data Policy:
//   DEMO (1-week): ALL data hard-deleted after expiry. Receipt generated.
//   FREE: MT keeps data and can use it (agreed in TOS). No scrubbing.
//   PAID (Growth/Scale): Full privacy. Data owned by customer. No MT usage.
//
// Usage:
//   import { DemoDataScrubber, DemoExpiryChecker, generateDeletionReceipt } from '../components/DemoDataScrubber';
//
//   // Auto-run on mount (for background cleanup jobs):
//   <DemoDataScrubber supabase={supabase} companyId={id} appName="Island Overhead Doors" autoRun />
//
//   // Manual trigger (for admin dashboard):
//   <DemoDataScrubber supabase={supabase} companyId={id} appName="Island Overhead Doors" />
//
//   // Check expiry without UI:
//   const status = await DemoExpiryChecker(supabase, companyId);
//   if (status.expired) { /* trigger scrub */ }
// ================================================================
import React, { useState, useEffect, useCallback } from 'react';

const SCRUBBER_VERSION = '1.0.0';

const DEFAULT_TABLES = [
  { name: 'master_jobs', filterCol: 'company_id' },
  { name: 'customers', filterCol: 'company_id' },
  { name: 'quotes', filterCol: 'company_id' },
  { name: 'invoices', filterCol: 'company_id' },
  { name: 'contacts', filterCol: 'company_id' },
  { name: 'company_profile', filterCol: 'id' },
  { name: 'app_events', filterCol: 'company_id' },
  { name: 'notes', filterCol: 'company_id' },
  { name: 'attachments', filterCol: 'company_id' },
  { name: 'crew_shifts', filterCol: 'company_id' },
  { name: 'inventory_items', filterCol: 'company_id' },
];

const DATA_CATEGORIES = [
  'Business profile and company information',
  'Customer and client records',
  'Job and service records',
  'Financial records (quotes, invoices)',
  'Communication records (notes, contacts)',
  'Operational data (schedules, shifts, inventory)',
  'Analytics and usage data',
  'File attachments',
];

function generateReceiptId() {
  const now = new Date();
  const date = now.toISOString().split('T')[0];
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `DEL-${date}-${rand}`;
}

export async function DemoExpiryChecker(supabase, companyId) {
  try {
    const { data, error } = await supabase
      .from('demo_accounts')
      .select('demo_start_date, demo_end_date, demo_status')
      .eq('company_id', companyId)
      .eq('demo_status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      return { expired: false, isDemo: false, error: error?.message || 'No active demo found' };
    }

    const now = new Date();
    const endDate = new Date(data.demo_end_date || new Date(data.demo_start_date).getTime() + 7 * 24 * 60 * 60 * 1000);
    const daysOverdue = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));

    return {
      isDemo: true,
      expired: now > endDate,
      daysOverdue: Math.max(0, daysOverdue),
      demoStartDate: data.demo_start_date,
      demoEndDate: endDate.toISOString(),
      status: data.demo_status,
    };
  } catch (e) {
    return { expired: false, isDemo: false, error: e.message };
  }
}

export function generateDeletionReceipt(receipt) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Data Deletion Receipt - ${receipt.receiptId}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #0F172A; color: #E2E8F0; padding: 40px; max-width: 700px; margin: 0 auto; }
  .header { text-align: center; border-bottom: 2px solid #334155; padding-bottom: 24px; margin-bottom: 24px; }
  .header h1 { font-size: 22px; color: #F8FAFC; margin: 0 0 8px 0; }
  .header .subtitle { color: #94A3B8; font-size: 13px; }
  .receipt-id { font-family: monospace; font-size: 18px; color: #38BDF8; background: #1E293B; padding: 12px 20px; border-radius: 8px; text-align: center; margin: 20px 0; letter-spacing: 2px; }
  .section { margin-bottom: 20px; }
  .section h2 { font-size: 14px; color: #94A3B8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
  .field { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #1E293B; }
  .field .label { color: #94A3B8; }
  .field .value { color: #F1F5F9; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; }
  th { text-align: left; padding: 8px 12px; background: #1E293B; color: #94A3B8; font-size: 12px; text-transform: uppercase; }
  td { padding: 8px 12px; border-bottom: 1px solid #1E293B; color: #E2E8F0; }
  .confirmation { background: #064E3B; border: 1px solid #059669; border-radius: 10px; padding: 20px; margin-top: 24px; text-align: center; }
  .confirmation p { margin: 0; line-height: 1.6; }
  .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #334155; color: #64748B; font-size: 12px; }
</style>
</head>
<body>
  <div class="header">
    <h1>PROOF OF DATA DELETION</h1>
    <div class="subtitle">Miskulin Technologies Inc. — Data Retention Compliance</div>
  </div>
  <div class="receipt-id">${receipt.receiptId}</div>
  <div class="section">
    <h2>Details</h2>
    <div class="field"><span class="label">Company</span><span class="value">${receipt.companyName || 'N/A'}</span></div>
    <div class="field"><span class="label">Application</span><span class="value">${receipt.appName}</span></div>
    <div class="field"><span class="label">Deletion Completed</span><span class="value">${new Date(receipt.completedAt).toLocaleString()}</span></div>
    <div class="field"><span class="label">Initiated By</span><span class="value">${receipt.initiatedBy}</span></div>
    <div class="field"><span class="label">Scrubber Version</span><span class="value">${receipt.scrubberVersion}</span></div>
    <div class="field"><span class="label">Total Records Deleted</span><span class="value">${receipt.totalRecordsDeleted}</span></div>
  </div>
  <div class="section">
    <h2>Tables Scrubbed</h2>
    <table>
      <thead><tr><th>Table</th><th>Records Deleted</th></tr></thead>
      <tbody>${Object.entries(receipt.recordsDeleted).map(([t, c]) => `<tr><td>${t}</td><td>${c}</td></tr>`).join('')}</tbody>
    </table>
  </div>
  <div class="section">
    <h2>Data Categories Removed</h2>
    <ul>${receipt.dataCategories.map(c => `<li>${c}</li>`).join('')}</ul>
  </div>
  <div class="confirmation">
    <p><strong>All data associated with ${receipt.companyName || 'the above company'} has been permanently removed from Miskulin Technologies systems.</strong></p>
    <p>No copies, backups, snapshots, or derivatives of this data remain.</p>
  </div>
  <div class="footer">
    <p>Miskulin Technologies Inc. | 121 Rainbow Falls Bay, Chestermere, AB T1X 0S5, Canada</p>
    <p>investor@miskulintechnologies.com | Generated ${new Date().toISOString()}</p>
  </div>
</body>
</html>`;
}

export default function DemoDataScrubber({
  supabase, companyId, appName = 'Unknown App', tables = DEFAULT_TABLES, autoRun = false, onComplete,
}) {
  const [status, setStatus] = useState('idle');
  const [receipt, setReceipt] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentTable: '' });
  const [error, setError] = useState(null);

  const runScrub = useCallback(async () => {
    if (!companyId || !supabase) return;
    setStatus('checking'); setError(null);
    const expiryStatus = await DemoExpiryChecker(supabase, companyId);
    if (!expiryStatus.isDemo) { setStatus('not_demo'); return; }
    if (!expiryStatus.expired) { setStatus('idle'); setError(`Demo still active. Expires: ${new Date(expiryStatus.demoEndDate).toLocaleDateString()}`); return; }
    setStatus('expired');
    let companyName = 'Unknown Company';
    try { const { data } = await supabase.from('company_profile').select('company_name').eq('id', companyId).single(); if (data?.company_name) companyName = data.company_name; } catch (e) { }
    setStatus('scrubbing');
    const recordsDeleted = {}; let totalDeleted = 0; const tablesScanned = [];
    setProgress({ current: 0, total: tables.length, currentTable: '' });
    for (let i = 0; i < tables.length; i++) {
      const table = tables[i];
      setProgress({ current: i + 1, total: tables.length, currentTable: table.name });
      try {
        const { count, error: countErr } = await supabase.from(table.name).select('id', { count: 'exact', head: true }).eq(table.filterCol, companyId);
        if (countErr) { recordsDeleted[table.name] = 0; tablesScanned.push(table.name); continue; }
        const recordCount = count || 0;
        if (recordCount > 0) {
          const { error: delErr } = await supabase.from(table.name).delete().eq(table.filterCol, companyId);
          if (delErr) { console.warn(`DemoScrubber: delete error on ${table.name}:`, delErr.message); recordsDeleted[table.name] = 0; }
          else { recordsDeleted[table.name] = recordCount; totalDeleted += recordCount; }
        } else { recordsDeleted[table.name] = 0; }
        tablesScanned.push(table.name);
      } catch (e) { console.warn(`DemoScrubber: error on ${table.name}:`, e.message); recordsDeleted[table.name] = 0; tablesScanned.push(table.name); }
    }
    const receiptData = { receiptId: generateReceiptId(), companyId, companyName, appName, tablesScanned, recordsDeleted, totalRecordsDeleted: totalDeleted, dataCategories: DATA_CATEGORIES, scrubberVersion: SCRUBBER_VERSION, initiatedBy: autoRun ? 'auto_expiry' : 'manual_admin', completedAt: new Date().toISOString() };
    try { await supabase.from('deletion_receipts').insert({ receipt_id: receiptData.receiptId, company_id: companyId, company_name: companyName, app_name: appName, tables_scanned: tablesScanned, records_deleted: recordsDeleted, total_records_deleted: totalDeleted, data_categories: DATA_CATEGORIES, scrubber_version: SCRUBBER_VERSION, initiated_by: receiptData.initiatedBy }); } catch (e) { console.warn('DemoScrubber: failed to store receipt:', e.message); }
    try { await supabase.from('demo_accounts').update({ demo_status: 'scrubbed', scrubbed_at: new Date().toISOString() }).eq('company_id', companyId).eq('demo_status', 'active'); } catch (e) { console.warn('DemoScrubber: failed to update demo status:', e.message); }
    setReceipt(receiptData); setStatus('done');
    if (onComplete) onComplete(receiptData);
  }, [supabase, companyId, appName, tables, autoRun, onComplete]);

  useEffect(() => { if (autoRun && companyId) { runScrub(); } }, [autoRun, companyId, runScrub]);

  const downloadReceipt = () => {
    if (!receipt) return;
    const html = generateDeletionReceipt(receipt);
    const blob = new Blob([html], { type: 'text/html' }); const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `deletion-receipt-${receipt.receiptId}.html`; a.click(); URL.revokeObjectURL(url);
  };

  const containerStyle = { padding: 24, background: '#0F172A', borderRadius: 14, color: '#E2E8F0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' };
  const btnPrimary = { padding: '12px 24px', borderRadius: 10, border: 'none', background: '#DC2626', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 };
  const btnSecondary = { padding: '12px 24px', borderRadius: 10, border: '1px solid #334155', background: '#1E293B', color: '#E2E8F0', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#1E293B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{status === 'done' ? '\u2705' : '\uD83D\uDDD1\uFE0F'}</div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 18, color: '#F8FAFC' }}>Demo Data Scrubber</div>
          <div style={{ fontSize: 13, color: '#94A3B8' }}>Permanently removes all demo company data with proof-of-deletion receipt</div>
        </div>
      </div>
      {status === 'idle' && !error && (<button onClick={runScrub} style={btnPrimary}>Run Demo Data Scrub</button>)}
      {status === 'idle' && error && (<div style={{ padding: 16, background: '#1E293B', borderRadius: 10, border: '1px solid #334155' }}><div style={{ fontSize: 14, color: '#FBBF24', fontWeight: 600 }}>{error}</div><button onClick={runScrub} style={{ ...btnSecondary, marginTop: 12 }}>Check Again</button></div>)}
      {status === 'checking' && (<div style={{ padding: 20, textAlign: 'center', color: '#94A3B8' }}><div style={{ fontSize: 14, fontWeight: 600 }}>Checking demo status...</div></div>)}
      {status === 'not_demo' && (<div style={{ padding: 16, background: '#1E293B', borderRadius: 10, border: '1px solid #334155' }}><div style={{ fontSize: 14, color: '#94A3B8' }}>This company is not on a demo trial. No scrubbing needed.</div></div>)}
      {(status === 'expired' || status === 'scrubbing') && (
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#F87171', marginBottom: 12 }}>Demo expired. Scrubbing data...</div>
          <div style={{ background: '#1E293B', borderRadius: 8, overflow: 'hidden', height: 8, marginBottom: 8 }}><div style={{ width: `${(progress.current / Math.max(progress.total, 1)) * 100}%`, height: '100%', background: '#DC2626', transition: 'width 0.3s ease' }} /></div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Table {progress.current}/{progress.total}: {progress.currentTable}</div>
        </div>
      )}
      {status === 'done' && receipt && (
        <div>
          <div style={{ padding: 20, background: '#064E3B', borderRadius: 12, border: '1px solid #059669', marginBottom: 16 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#34D399', marginBottom: 8 }}>Data Scrub Complete</div>
            <div style={{ fontSize: 13, color: '#A7F3D0', lineHeight: 1.6 }}><strong>{receipt.totalRecordsDeleted}</strong> records permanently deleted across{' '}<strong>{receipt.tablesScanned.length}</strong> tables.</div>
          </div>
          <div style={{ background: '#1E293B', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 16, color: '#38BDF8', textAlign: 'center', marginBottom: 12, letterSpacing: 2 }}>{receipt.receiptId}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
              <div><span style={{ color: '#64748B' }}>Company:</span> <span style={{ color: '#E2E8F0' }}>{receipt.companyName}</span></div>
              <div><span style={{ color: '#64748B' }}>App:</span> <span style={{ color: '#E2E8F0' }}>{receipt.appName}</span></div>
              <div><span style={{ color: '#64748B' }}>Deleted:</span> <span style={{ color: '#E2E8F0' }}>{receipt.totalRecordsDeleted} records</span></div>
              <div><span style={{ color: '#64748B' }}>When:</span> <span style={{ color: '#E2E8F0' }}>{new Date(receipt.completedAt).toLocaleString()}</span></div>
            </div>
          </div>
          <div style={{ background: '#1E293B', borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#94A3B8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Tables Scrubbed</div>
            {Object.entries(receipt.recordsDeleted).map(([table, count]) => (<div key={table} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #0F172A', fontSize: 13 }}><span style={{ color: '#CBD5E1' }}>{table}</span><span style={{ color: count > 0 ? '#F87171' : '#475569', fontWeight: 600 }}>{count > 0 ? `-${count}` : '0'}</span></div>))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}><button onClick={downloadReceipt} style={btnSecondary}>Download Proof of Deletion</button></div>
        </div>
      )}
      {status === 'error' && (<div style={{ padding: 16, background: '#450A0A', borderRadius: 10, border: '1px solid #DC2626' }}><div style={{ fontSize: 14, color: '#FCA5A5', fontWeight: 600 }}>{error}</div></div>)}
    </div>
  );
}
